/**
 * Helpers de formato de fecha/hora que respetan la zona horaria de la
 * empresa. Todos reciben un `tz` opcional (IANA); si no se pasa usan
 * la del navegador (que es el comportamiento por defecto de Intl).
 *
 * Uso típico en vistas admin:
 *   const { currentCompany } = useCompany();
 *   const tz = currentCompany?.timezone;
 *   formatDateLong(order.created_at, tz);
 */

/**
 * Devuelve el ID corto y consistente que se muestra en TODA la app
 * para identificar un pedido (cliente, admin y página pública de
 * tracking). Tomamos los últimos 6 caracteres del UUID en MAYÚSCULAS,
 * sin el prefijo `#` (los callers lo ponen). 6 caracteres son
 * suficientes para que el cliente lea/escriba el ID por WhatsApp y a
 * la vez evita confusión con códigos largos.
 */
export function shortOrderId(orderId) {
  if (!orderId) return "";
  return String(orderId).slice(-6).toUpperCase();
}

export function formatDateLong(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: tz || undefined,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return new Date(iso).toLocaleString("es");
  }
}

export function formatDateShort(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: tz || undefined,
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return new Date(iso).toLocaleString("es");
  }
}

export function formatWeekdayLong(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      timeZone: tz || undefined,
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
  } catch {
    return new Date(iso).toLocaleDateString("es");
  }
}

export function formatDayMonth(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      timeZone: tz || undefined,
      day: "2-digit",
      month: "short",
    });
  } catch {
    return new Date(iso).toLocaleDateString("es");
  }
}

/**
 * Devuelve YYYY-MM-DD del ISO dado en la zona horaria pedida. Útil
 * para agrupar por día (ej. exports, gráficas).
 */
export function isoDateInTz(iso, tz) {
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || undefined,
      year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date(iso));
    // en-CA gives "YYYY-MM-DD"
    return parts;
  } catch {
    return new Date(iso).toISOString().slice(0, 10);
  }
}


/**
 * Estado de "urgencia" de un pedido programado, usado para colorear la
 * etiqueta de fecha en las vistas admin.
 *
 * @param {{ when?: string|null, scheduled_at?: string|null, status?: string }} o
 * @param {string=} tz IANA timezone de la empresa
 * @returns {"immediate"|"overdue"|"today"|"upcoming"}
 *   - immediate: pedido NO programado (entrega inmediata)
 *   - overdue:   programado, ya pasó la fecha y aún no se entregó
 *   - today:     programado para hoy (zona horaria empresa)
 *   - upcoming:  programado para un día futuro
 */
export function scheduleUrgency(o, tz) {
  if (!o || o.when !== "scheduled" || !o.scheduled_at) return "immediate";
  const now = new Date();
  const target = new Date(o.scheduled_at);
  const closed = o.status === "delivered" || o.status === "cancelled";
  if (!closed && target.getTime() < now.getTime()) return "overdue";
  if (isoDateInTz(o.scheduled_at, tz) === isoDateInTz(now.toISOString(), tz)) {
    return "today";
  }
  return "upcoming";
}
